import React from "react";
import {
  Box,
  Container,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  Heading,
  Grid,
  Image,
  useColorModeValue,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import ServiceDetailsHeader from "../components/Common/ImageHeader";
import HeaderLanguageSelector from "../components/Common/HeaderLanguageSelector";
import { useLanguage } from "../contexts/LanguageContext";
import { getTranslation } from "../translations";
import ExploreSection from "../components/ProductDetails/ExploreSection";

const PartnershipsCollaborations = () => {
  const navigate = useNavigate();
  const textColor = useColorModeValue("gray.600", "gray.300");
  const headingColor = useColorModeValue("gray.900", "white");
  const { selectedLanguage } = useLanguage();

  // Get base URL from environment variable
  const siteBaseUrl = process.env.REACT_APP_SITE_BASE_URL;

  const handleBreadcrumbClick = (href) => {
    if (href && href !== "#") {
      navigate(href);
    }
  };

  // Get translations for current language
  const t = (path) => getTranslation(selectedLanguage, path);

  // How We Can Help data
  const howWeCanHelpData = t("howWeCanHelp.services").map((service, index) => ({
    ...service,
    bgColor: [
      "#EBFCFF",
      "#F7EEFF",
      "#E9EBFF",
      "#FFF4F0",
      "#EBFCFF",
      "#F7EEFF",
      "#E9EBFF",
      "#FFF4F0",
    ][index],
    color: [
      "#00B894",
      "#6C5CE7",
      "#0984E3",
      "#E17055",
      "#00CEC9",
      "#A29BFE",
      "#0984E3",
      "#E17055",
    ][index],
    icon: [
      "/png/partnership/hw_1.svg",
      "/png/partnership/hw_2.svg",
      "/png/partnership/hw_3.svg",
      "/png/partnership/hw_4.svg",
      "/png/partnership/hw_5.svg",
      "/png/partnership/hw_6.svg",
      "/png/partnership/hw_7.svg",
      "/png/partnership/hw_8.svg",
    ][index],
  }));

  // Trusted Partner Statistics data
  const trustedPartnerStats = t("trustedPartner.stats").map((stat, index) => ({
    ...stat,
    icon: [
      "/png/partnership/pc_1.png",
      "/png/partnership/pc_2.png",
      "/png/partnership/pc_3.png",
    ][index],
  }));

  // Collaboration modes data
  const collaborationModes = t("collaboration.modes").map((mode, index) => ({
    ...mode,
    number: String(index + 1).padStart(2, "0"),
  }));

  // Track Record Items data
  const trackRecordItems = t("trackRecord.items").map((item) => ({
    ...item,
    linkUrl: item.linkUrl ? `${siteBaseUrl}${item.linkUrl}` : undefined,
  }));

  return (
    <>
      {/* Header Section */}
      <ServiceDetailsHeader
        title={t("header.title")}
        subtitle={t("header.subtitle")}
        backgroundImage="/png/client-partner.png"
        breadcrumbItems={[
          { label: t("header.breadcrumb.home"), href: "/" },
          {
            label: t("header.breadcrumb.current"),
            href: "#",
            isCurrentPage: true,
          },
        ]}
        onBreadcrumbClick={handleBreadcrumbClick}
        topRightComponent={<HeaderLanguageSelector />}
      />

      <Box
        py={{ base: 4, md: 8 }}
        bgImage="url('/png/background.png')"
        bgSize="cover"
        bgPosition="center"
        bgRepeat="no-repeat"
      >
        {/* Introduction Section */}
        <Container maxW="7xl" px={{ base: 4, md: 8 }}>
          <VStack spacing={4} align="center" textAlign="start" mb={16}>
            <Text
              fontSize={{ base: "md", md: "lg" }}
              color={textColor}
              textAlign={"justify"}
              maxW="7xl"
              lineHeight="1.2"
            >
              {t("introduction.paragraph1")}
            </Text>

            <Text
              fontSize={{ base: "md", md: "lg" }}
              color={textColor}
              maxW="7xl"
              textAlign={"justify"}
              lineHeight="1.2"
            >
              {t("introduction.paragraph2")}
            </Text>
            {t("introduction.paragraph3") &&
              t("introduction.paragraph3").trim() &&
              t("introduction.paragraph3") !== "introduction.paragraph3" && (
                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  color={textColor}
                  maxW="7xl"
                  textAlign={"justify"}
                  lineHeight="1.2"
                >
                  {t("introduction.paragraph3")}
                </Text>
              )}
            {t("introduction.paragraph4") &&
              t("introduction.paragraph4").trim() &&
              t("introduction.paragraph4") !== "introduction.paragraph4" && (
                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  color={textColor}
                  maxW="7xl"
                  textAlign={"justify"}
                  lineHeight="1.2"
                >
                  {t("introduction.paragraph4")}
                </Text>
              )}
          </VStack>
          <VStack spacing={0} align="center" mb={16}>
            <Heading
              fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
              color={headingColor}
              textAlign="center"
              fontWeight="bold"
              mb={4}
            >
              {t("howWeCanHelp.title").split(" ").slice(0, -2).join(" ")}{" "}
              <Box as="span" color="#000B8C">
                {t("howWeCanHelp.title").split(" ").slice(-2).join(" ")}
              </Box>
            </Heading>

            <Text
              fontSize={{ base: "md", md: "lg" }}
              color={"#808080"}
              textAlign="center"
              maxW="6xl"
              mb={8}
            >
              {t("howWeCanHelp.subtitle")}
            </Text>

            {/* Services Grid */}
            <Box
              display="grid"
              gridTemplateColumns={{
                base: "1fr",
                md: "1fr 1fr",
                lg: "1fr 1fr",
              }}
              w="100%"
              maxW="7xl"
              overflow="hidden"
              // boxShadow="sm"
            >
              {/* Left Column */}
              <VStack spacing={0} align="stretch" h="full" pr={8}>
                {howWeCanHelpData
                  .slice(0, Math.ceil(howWeCanHelpData.length / 2))
                  .map((item, index) => (
                    <Box
                      key={index}
                      backgroundColor={item.bgColor}
                      p={6}
                      borderTopWidth={3}
                      borderTopColor={item.color}
                      borderTopStyle={"solid"}
                      h="auto"
                    >
                      <HStack spacing={4} align="center">
                        <Box
                          w={"40px"}
                          h={"40px"}
                          borderRadius="lg"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          flexShrink={0}
                        >
                          <img
                            src={item.icon}
                            alt={item.title}
                            style={{
                              width: "35px",
                              height: "35px",
                            }}
                          />
                        </Box>
                        <VStack align="flex-start" spacing={2} flex={1}>
                          <Heading
                            fontSize="lg"
                            color="#2D3748"
                            fontWeight="bold"
                            lineHeight="1.2"
                          >
                            {item.title}
                          </Heading>
                          <Text
                            fontSize="sm"
                            color="#4A5568"
                            lineHeight="1.2"
                            textAlign={"justify"}
                          >
                            {item.description}
                          </Text>
                        </VStack>
                      </HStack>
                    </Box>
                  ))}
              </VStack>

              {/* Right Column */}
              <VStack spacing={0} align="stretch" h="full" pl={8}>
                {howWeCanHelpData
                  .slice(Math.ceil(howWeCanHelpData.length / 2))
                  .map((item, index) => (
                    <Box
                      key={index}
                      p={4}
                      backgroundColor={item.bgColor}
                      borderTopWidth={3}
                      borderTopColor={item.color}
                      borderTopStyle={"solid"}
                      h="auto"
                    >
                      <HStack spacing={4} align="center">
                        <Box
                          w={"40px"}
                          h={"40px"}
                          borderRadius="lg"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          flexShrink={0}
                        >
                          <img
                            src={item.icon}
                            alt={item.title}
                            style={{
                              width: "35px",
                              height: "35px",
                            }}
                          />
                        </Box>
                        <VStack align="flex-start" spacing={2} flex={1}>
                          <Heading
                            fontSize="lg"
                            color="#2D3748"
                            fontWeight="bold"
                            lineHeight="1.45"
                          >
                            {item.title}
                          </Heading>
                          <Text
                            fontSize="sm"
                            color="#4A5568"
                            lineHeight="1.2"
                            textAlign={"justify"}
                          >
                            {item.description}
                          </Text>
                        </VStack>
                      </HStack>
                    </Box>
                  ))}
              </VStack>
            </Box>
          </VStack>

          {selectedLanguage !== "English" && (
            <Box mt={8} mb={8} display="flex" justifyContent="center">
              <VStack spacing={4} align="center">
                {/* Hashtags Section */}
                {t("contact.hashtags") && (
                  <Text
                    fontSize={{ base: "sm", md: "md" }}
                    color="#666"
                    textAlign="center"
                    maxW="800px"
                    lineHeight="1.6"
                    mb={4}
                  >
                    {t("contact.hashtags")}
                  </Text>
                )}
                
                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  color={textColor}
                  mr={4}
                  align="center"
                >
                  {t("contact.inquiryText")}
                </Text>
                <Box
                  as="button"
                  px={8}
                  py={4}
                  bg="#000B8C"
                  color="white"
                  fontWeight="bold"
                  borderRadius="2xl"
                  fontSize={{ base: "md", md: "lg" }}
                  _hover={{ bg: "#00136b" }}
                  onClick={() => navigate("/contact?form=partnership")}
                >
                  {t("contact.buttonText")}
                </Box>
              </VStack>
            </Box>
          )}

          {/* Conditional rendering - hide remaining content for Korean language */}
          {selectedLanguage === "English" && (
            <>
              {/* What Makes Us a Trusted Partner Section */}
              <VStack spacing={0} align="center" mb={8}>
                <Heading
                  fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                  color={headingColor}
                  textAlign="center"
                  fontWeight="bold"
                  mb={4}
                >
                  {t("trustedPartner.title").split(" ").slice(0, -2).join(" ")}{" "}
                  <Box as="span" color="#000B8C">
                    {t("trustedPartner.title").split(" ").slice(-2).join(" ")}
                  </Box>
                </Heading>

                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  color={"#808080"}
                  textAlign="center"
                  maxW="7xl"
                  mb={12}
                  px={4}
                >
                  {t("trustedPartner.subtitle")}
                </Text>

                {/* Top Row Statistics */}
                <SimpleGrid
                  columns={{ base: 1, md: 3 }}
                  spacing={0}
                  w="100%"
                  maxW="7xl"
                  mb={8}
                >
                  {trustedPartnerStats.slice(0, 3).map((stat, index) => (
                    <VStack
                      key={index}
                      spacing={4}
                      align="center"
                      p={8}
                      position="relative"
                      _after={
                        index < 2
                          ? {
                              content: '""',
                              position: "absolute",
                              right: 0,
                              top: "20%",
                              height: "60%",
                              width: "1px",
                              bg: "#CBD5E0",
                              display: { base: "none", md: "block" },
                            }
                          : {}
                      }
                    >
                      <Box
                        w="60px"
                        h="60px"
                        borderRadius="full"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        mb={2}
                      >
                        <img
                          src={stat.icon}
                          alt={stat.title}
                          style={{
                            width: "60px",
                            height: "60px",
                          }}
                        />
                      </Box>

                      <Heading
                        fontSize={{ base: "2xl", md: "3xl" }}
                        color="#000B8C"
                        fontWeight="bold"
                        textAlign="center"
                      >
                        {stat.number}
                      </Heading>

                      <Text
                        fontSize="sm"
                        color="#4A5568"
                        textAlign="center"
                        maxW="250px"
                        lineHeight="1.5"
                      >
                        {stat.title}
                      </Text>
                    </VStack>
                  ))}
                </SimpleGrid>

                {/* Track Record Text */}
                {/* <Text
              fontSize={{ base: "xl", md: "2xl" }}
              color="#2D3748"
              textAlign="center"
              fontStyle="italic"
              mb={8}
              fontWeight="bold"
            >
              Our track record speaks for itself :
            </Text> */}

                {/* Second Row Statistics */}
                {/* <SimpleGrid
              columns={{ base: 1, md: 3 }}
              spacing={0}
              w="100%"
              maxW="7xl"
              mb={8}
            >
              {trustedPartnerStats.slice(3, 6).map((stat, index) => (
                <VStack
                  key={index}
                  spacing={4}
                  align="center"
                  p={8}
                  position="relative"
                  _after={
                    index < 2
                      ? {
                          content: '""',
                          position: "absolute",
                          right: 0,
                          top: "20%",
                          height: "60%",
                          width: "1px",
                          bg: "#CBD5E0",
                          display: { base: "none", md: "block" },
                        }
                      : {}
                  }
                >
                  <Box
                    w="60px"
                    h="60px"
                    borderRadius="full"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    mb={2}
                  >
                    <img
                      src={stat.icon}
                      alt={stat.title}
                      style={{
                        width: "60px",
                        height: "60px",
                      }}
                    />
                  </Box>

                  <Heading
                    fontSize={{ base: "2xl", md: "3xl" }}
                    color="#000B8C"
                    fontWeight="bold"
                    textAlign="center"
                  >
                    {stat.number}
                  </Heading>

                  <Text
                    fontSize="sm"
                    color="#4A5568"
                    textAlign="center"
                    maxW="250px"
                    lineHeight="1.5"
                  >
                    {stat.title}
                  </Text>
                </VStack>
              ))}
            </SimpleGrid> */}

                {/* Third Row Statistics */}
                {/* <SimpleGrid
              columns={{ base: 1, md: 3 }}
              spacing={0}
              w="100%"
              maxW="7xl"
              mb={8}
            >
              {trustedPartnerStats.slice(6, 9).map((stat, index) => (
                <VStack
                  key={index}
                  spacing={4}
                  align="center"
                  p={8}
                  position="relative"
                  _after={
                    index < 2
                      ? {
                          content: '""',
                          position: "absolute",
                          right: 0,
                          top: "20%",
                          height: "60%",
                          width: "1px",
                          bg: "#CBD5E0",
                          display: { base: "none", md: "block" },
                        }
                      : {}
                  }
                >
                  <Box
                    w="60px"
                    h="60px"
                    borderRadius="full"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    mb={2}
                  >
                    <img
                      src={stat.icon}
                      alt={stat.title}
                      style={{
                        width: "60px",
                        height: "60px",
                      }}
                    />
                  </Box>

                  <Heading
                    fontSize={{ base: "2xl", md: "3xl" }}
                    color="#000B8C"
                    fontWeight="bold"
                    textAlign="center"
                  >
                    {stat.number}
                  </Heading>

                  <Text
                    fontSize="sm"
                    color="#4A5568"
                    textAlign="center"
                    maxW="250px"
                    lineHeight="1.5"
                  >
                    {stat.title}
                  </Text>
                </VStack>
              ))}
            </SimpleGrid> */}

                {/* Fourth Row Statistics - Centered Layout */}
                {/* <Box display="flex" justifyContent="center" w="100%" maxW="7xl">
              <Box
                display="grid"
                gridTemplateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
                gap={{ base: 0, md: 8 }}
                justifyContent="center"
                maxW="700px"
              >
                {trustedPartnerStats.slice(9).map((stat, index) => (
                  <VStack
                    key={index}
                    spacing={4}
                    align="center"
                    p={8}
                    position="relative"
                    _after={
                      index < 1
                        ? {
                            content: '""',
                            position: "absolute",
                            right: { base: "none", md: "-16px" },
                            top: "20%",
                            height: "60%",
                            width: "1px",
                            bg: "#CBD5E0",
                            display: { base: "none", md: "block" },
                          }
                        : {}
                    }
                  >
                    <Box
                      w="60px"
                      h="60px"
                      borderRadius="full"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      mb={2}
                    >
                      <img
                        src={stat.icon}
                        alt={stat.title}
                        style={{
                          width: "60px",
                          height: "60px",
                        }}
                      />
                    </Box>

                    <Heading
                      fontSize={{ base: "2xl", md: "3xl" }}
                      color="#000B8C"
                      fontWeight="bold"
                      textAlign="center"
                    >
                      {stat.number}
                    </Heading>

                    <Text
                      fontSize="sm"
                      color="#4A5568"
                      textAlign="center"
                      maxW="250px"
                      lineHeight="1.5"
                    >
                      {stat.title}
                    </Text>
                  </VStack>
                ))}
              </Box>
            </Box> */}
              </VStack>

              <Box py={8}>
                <Heading
                  fontSize={{ base: "xl", md: "2xl" }}
                  color="#2D3748"
                  fontStyle="italic"
                  fontWeight="bold"
                  textAlign="center"
                  w="100%"
                >
                  {t("trackRecord.title")}
                </Heading>
                <Grid
                  templateColumns={{ base: "1fr", lg: "1fr 1fr" }}
                  gap={8}
                  alignItems="center"
                  maxW="7xl"
                  mx="auto"
                >
                  {/* Left Side - Image */}
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    p={{ base: 4, md: 8 }}
                  >
                    <Image
                      src="/png/pc_illu.png"
                      alt="Track Record Illustration"
                      maxW="100%"
                      maxH={{ base: "350px", md: "450px", lg: "550px" }}
                      height="auto"
                      borderRadius="lg"
                      loading="lazy"
                      fallbackSrc="/png/default-product.png"
                      objectFit="contain"
                    />
                  </Box>

                  {/* Right Side - Content with Icons */}
                  <VStack spacing={6} align="start" p={{ base: 4, md: 8 }}>
                    {/* Content Items with Icons */}
                    <VStack spacing={4} align="start" w="100%">
                      {trackRecordItems.map((item, index) => (
                        <HStack key={index} align="center" spacing={4}>
                          <Box
                            minW="35px"
                            h="35px"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            mt={1}
                          >
                            <Image
                              src={item.icon}
                              alt={item.alt}
                              w="35px"
                              h="35px"
                              fallbackSrc="/png/default-icon.png"
                            />
                          </Box>
                          <VStack align="start" spacing={1} flex={1}>
                            <Text
                              fontSize="md"
                              color="#2D3748"
                              lineHeight="1.2"
                              textAlign="justify"
                            >
                              {item.text}{" "}
                              {item.hasLink && (
                                <Text
                                  as="span"
                                  color="#000B8C"
                                  textDecoration="underline"
                                  cursor="pointer"
                                  onClick={() =>
                                    window.open(
                                      item.linkUrl,
                                      "_blank",
                                      "noopener,noreferrer"
                                    )
                                  }
                                  _hover={{ color: "#0056b3" }}
                                >
                                  {item.linkText}
                                </Text>
                              )}
                            </Text>
                          </VStack>
                        </HStack>
                      ))}
                    </VStack>
                  </VStack>
                </Grid>
              </Box>

              {/* Collaboration Modes Section */}
              <VStack spacing={0} align="center" mb={0} mt={6}>
                <Heading
                  fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                  color={headingColor}
                  textAlign="center"
                  fontWeight="bold"
                >
                  How We Can {""}
                  <Box as="span" color="#000B8C">
                    Collaborate With You
                  </Box>{" "}
                </Heading>

                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  color={"#808080"}
                  textAlign="center"
                  mt={2}
                  py={4}
                  maxW="7xl"
                >
                  In a dynamic and diverse business landscape, different forms
                  of partnership are needed depending on the needs of the
                  project at hand. We adapt to that by providing you with
                  options of potential modes of collaboration such as:
                </Text>

                {/* Collaboration Modes Grid */}
                <Box
                  display="flex"
                  flexWrap="wrap"
                  gap={8}
                  mt={6}
                  w="100%"
                  justifyContent="center"
                  alignItems="stretch"
                >
                  {collaborationModes.map((mode, index) => (
                    <Box
                      key={index}
                      w="100%"
                      backgroundColor={"white"}
                      maxW="400px"
                      minW={{ base: "100%", md: "350px" }}
                      flex={{
                        base: "1 1 100%",
                        md: "1 1 calc(50% - 16px)",
                        lg: "1 1 calc(33.333% - 22px)",
                      }}
                    >
                      <HStack
                        bg={"white"}
                        p={6}
                        borderRadius="xl"
                        borderWidth={1}
                        borderColor="#D4D4D4"
                        spacing={0}
                        align="center"
                        h="full"
                        _hover={{
                          transform: "translateY(-4px)",
                          boxShadow: "lg",
                          transition: "all 0.3s ease",
                        }}
                      >
                        <VStack spacing={2} align="center" minW="80px">
                          <Box
                            bg="#000B8C"
                            color="white"
                            borderRadius="full"
                            w="40px"
                            h="40px"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            fontSize="sm"
                            fontWeight="bold"
                          >
                            {mode.number}
                          </Box>
                          <Box fontSize="2xl">{mode.icon}</Box>
                        </VStack>

                        <VStack align="start" spacing={2} flex={1}>
                          <Heading
                            fontSize="lg"
                            color={headingColor}
                            fontWeight="bold"
                          >
                            {mode.title}
                          </Heading>
                          <Text
                            fontSize="sm"
                            color={textColor}
                            lineHeight="1.6"
                          >
                            {mode.description}
                          </Text>
                        </VStack>
                      </HStack>
                    </Box>
                  ))}
                </Box>
              </VStack>
              <Text
                fontSize={{ base: "md", md: "lg" }}
                color="#4A5568"
                lineHeight="1.8"
                pb={2}
                textAlign="center"
                mt={2}
                maxW="7xl"
              >
                {t("collaboration.conclusion")}
              </Text>
              <ExploreSection />

              {/* Trust Section */}
              {/* <VStack spacing={6} align="center" mb={8} mt={16}>
            <Heading
              fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
              color={headingColor}
              textAlign="center"
              fontWeight="bold"
            >
              What Makes Us a{" "}
              <Box as="span" color="#000B8C">
                Trusted Partner
              </Box>
            </Heading>

            <SimpleGrid
              columns={{ base: 1, md: 2, lg: 5 }}
              spacing={8}
              w="100%"
            >
              {trustIndicators.map((indicator, index) => (
                <VStack
                  key={index}
                  bg={cardBg}
                  p={6}
                  borderRadius="xl"
                  spacing={4}
                  align="center"
                  textAlign="center"
                  minH="200px"
                  justify="center"
                  _hover={{
                    transform: "translateY(-4px)",
                    boxShadow: "lg",
                    transition: "all 0.3s ease",
                  }}
                >
                  <Box fontSize="3xl">{indicator.icon}</Box>
                  {indicator.title && (
                    <Heading fontSize="3xl" color="#000B8C" fontWeight="bold">
                      {indicator.title}
                    </Heading>
                  )}
                  <Text fontSize="sm" color={textColor} lineHeight="1.6">
                    {indicator.subtitle}
                  </Text>
                </VStack>
              ))}
            </SimpleGrid>
          </VStack> */}
            </>
          )}
        </Container>
      </Box>
    </>
  );
};

export default PartnershipsCollaborations;
